import {
  ComponentProps,
  forwardRef,
  Fragment,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from "react";
import { isNil } from "lodash";
import { ChangeHandler } from "react-hook-form";
import { Input, InputWrapper, Symbol } from "./__styles__/TextInput";
import Wrapper, { WrapperProps } from "./Wrapper";

// we override some of these props with our own definitions
// for instance, size is normally an integer for <input>
// element but we're using it here as an enum for the style
type StrippedDownInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size" | "value" | "onChange" | "css"
>;

export const SymbolComponent = ({
  value,
  postfix,
  size,
}: {
  value?: Maybe<string | ReactNode>;
  postfix?: true;
  size?: ComponentProps<typeof Symbol>["size"];
}) => {
  if (isNil(value)) return null;

  return (
    <Symbol postfix={postfix} size={size}>
      {value}
    </Symbol>
  );
};

/**
 * @deprecated Use `TextField` and `HookFormTextField` instead.
 */
export type Props = StrippedDownInputProps & {
  name: string;
  type?: string;
  disabled?: boolean;
  readonly?: boolean;
  size?: "smaller" | "small" | "medium" | "large";
  error?: Maybe<string>;
  hasErrorWithoutText?: boolean;
  onFocus?: () => void;
  value?: Maybe<string | number>;
  tooltip?: string;
  postfix?: string | ReactNode;
  prefix?: string | ReactNode;
  children?: WrapperProps["addon"];
} & Pick<
    WrapperProps,
    | "labelSize"
    | "compactLabel"
    | "label"
    | "required"
    | "description"
    | "helperText"
    | "labelDecorator"
  >;

/** @deprecated Use `TextField` and `HookFormTextField` instead. */
const TextInput = ({
  name,
  type = "text",
  value = null,
  onChange,
  label = null,
  compactLabel = false,
  disabled = false,
  readonly = false,
  size = "medium",
  helperText,
  error,
  hasErrorWithoutText,
  children,
  required,
  description,
  prefix,
  postfix,
  labelSize,
  tooltip,
  labelDecorator,
  ...props
}: Props & {
  onChange?: (value: string) => void;
}) => {
  const InputWrapperComponent = !!prefix || !!postfix ? InputWrapper : Fragment;

  return (
    <Wrapper
      label={label}
      name={name}
      error={error}
      addon={children}
      required={required}
      description={description}
      compactLabel={compactLabel}
      labelSize={labelSize}
      tooltip={tooltip}
      helperText={helperText}
      labelDecorator={labelDecorator}
      printValue={`${prefix ?? ""} ${value} ${postfix ?? ""}`}
    >
      <InputWrapperComponent>
        <SymbolComponent value={prefix} />
        <Input
          type={type}
          name={name}
          value={value ?? ""}
          onChange={evt => onChange?.(evt.target.value)}
          isDisabled={disabled}
          isReadonly={readonly}
          disabled={disabled || readonly}
          id={name}
          error={!!error || hasErrorWithoutText}
          size={size}
          prefixed={!!prefix}
          postfixed={!!postfix}
          // prevents the input from increasing / decreasing
          // the value when the user scrolls *inside* the input
          onWheel={event => event.currentTarget.blur()}
          {...props}
        />
        <SymbolComponent value={postfix} postfix={true} size={size} />
      </InputWrapperComponent>
    </Wrapper>
  );
};

/** @deprecated Use `HookFormTextField` from `TextField.tsx` instead. */
export const ReactHookFormTextInput = forwardRef(
  (
    {
      name,
      type = "text",
      onChange,
      label = null,
      compactLabel = false,
      disabled = false,
      size = "medium",
      error,
      hasErrorWithoutText,
      children,
      required,
      description,
      expandWithError = true,
      tooltip,
      postfix,
      prefix,
      readonly,
      helperText,
      ...props
    }: Omit<Props, "value"> & {
      onChange?: ChangeHandler;
      expandWithError?: boolean;
    },
    ref: Ref<HTMLInputElement>,
  ) => {
    const InputWrapperComponent =
      !!prefix || !!postfix ? InputWrapper : Fragment;

    return (
      <Wrapper
        label={label}
        compactLabel={compactLabel}
        name={name}
        error={error}
        addon={children}
        required={required}
        description={description}
        expandWithError={expandWithError}
        tooltip={tooltip}
        helperText={helperText}
      >
        <InputWrapperComponent>
          <SymbolComponent value={prefix} size={size} />
          <Input
            type={type}
            name={name}
            onChange={onChange}
            disabled={disabled}
            id={name}
            error={!!error || hasErrorWithoutText}
            size={size}
            ref={ref}
            postfixed={!!postfix}
            prefixed={!!prefix}
            isReadonly={readonly}
            {...props}
          />
          <SymbolComponent value={postfix} postfix={true} size={size} />
        </InputWrapperComponent>
      </Wrapper>
    );
  },
);

export default TextInput;
